import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AuditTrail = ({ userId, auditLogs }) => {
  const [filterType, setFilterType] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('7days');

  const typeOptions = [
    { value: 'all', label: 'All Activities' },
    { value: 'login', label: 'Login Events' },
    { value: 'permission', label: 'Permission Changes' },
    { value: 'profile', label: 'Profile Updates' },
    { value: 'data', label: 'Data Modifications' },
  ];

  const periodOptions = [
    { value: '24hours', label: 'Last 24 Hours' },
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'login':
        return 'LogIn';
      case 'logout':
        return 'LogOut';
      case 'permission':
        return 'Shield';
      case 'profile':
        return 'User';
      case 'data':
        return 'Database';
      case 'export':
        return 'Download';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'login':
        return 'text-success';
      case 'logout':
        return 'text-muted-foreground';
      case 'permission':
        return 'text-warning';
      case 'profile':
        return 'text-primary';
      case 'data':
        return 'text-accent';
      case 'export':
        return 'text-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (date) => {
    return new Date(date)?.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const filteredLogs = auditLogs?.filter((log) => {
    if (filterType !== 'all' && log?.type !== filterType) return false;

    const logDate = new Date(log.timestamp);
    const now = new Date();
    const diffMs = now - logDate;

    switch (filterPeriod) {
      case '24hours':
        return diffMs <= 86400000;
      case '7days':
        return diffMs <= 604800000;
      case '30days':
        return diffMs <= 2592000000;
      case '90days':
        return diffMs <= 7776000000;
      default:
        return true;
    }
  });

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Audit Trail
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Complete activity history and security logs
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={() => console.log('Export audit logs')}
          >
            Export Logs
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Activity Type"
            options={typeOptions}
            value={filterType}
            onChange={setFilterType}
          />
          <Select
            label="Time Period"
            options={periodOptions}
            value={filterPeriod}
            onChange={setFilterPeriod}
          />
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
          {filteredLogs?.length === 0 ? (
            <div className="text-center py-8">
              <Icon
                name="FileSearch"
                size={48}
                className="mx-auto text-muted-foreground mb-3"
              />
              <p className="text-sm text-muted-foreground">
                No audit logs found for selected filters
              </p>
            </div>
          ) : (
            filteredLogs?.map((log) => (
              <div
                key={log?.id}
                className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-accent/5 transition-colors"
              >
                <div
                  className={`w-10 h-10 rounded-full bg-muted/30 flex items-center justify-center flex-shrink-0 ${getActivityColor(
                    log?.type
                  )}`}
                >
                  <Icon name={getActivityIcon(log?.type)} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-foreground">
                        {log?.action}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {log?.description}
                      </p>
                      {log?.details && (
                        <div className="mt-2 p-2 bg-muted/30 rounded text-xs font-data text-muted-foreground">
                          {log?.details}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-muted-foreground">
                        {formatTimestamp(log?.timestamp)}
                      </p>
                      {log?.ipAddress && (
                        <p className="text-xs text-muted-foreground mt-1">
                          IP: {log?.ipAddress}
                        </p>
                      )}
                    </div>
                  </div>
                  {log?.performedBy && (
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <Icon name="User" size={12} />
                      <span>Performed by: {log?.performedBy}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;